// React
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import Navigation from 'ui/components/DayPicker/Navigation'
import InputField from 'ui/components/InputField'
import PickerDay from 'ui/components/Picker/Day'
import Popover from 'ui/components/Popover'
import Icon from 'ui/components/Icon'
import BDatePickerInput from 'ui/blocks/DatePickerInput'
import BDayPicker from 'ui/blocks/DayPicker'
import H3 from 'ui/elements/H3'
import TimeTable from './TimeTable'
import PortalWrapper from 'ui/components/PortalWrapper'
import BProductAvailabilityCalendar from 'store/modules/product/blocks/ProductAvailabilityCalendar'
import Div from 'ui/elements/Div'

// Libraries
import DayPicker, { MomentLocaleUtils } from 'react-day-picker'
import debounce from 'lodash/debounce'
import { CSSTransition, SwitchTransition, config } from 'react-transition-group'

// Shared
import { isDayClosed } from 'ui/components/Picker/utils'
import useIsMobile from 'shared/utils/useIsMobile'
import { withTranslation } from 'shared/utils/withTranslation'
import moment, { localMoment } from 'shared/lib/moment'
import AvailabilityActions from 'store/v2/redux/actions/availability'
import { localMomentInUTC } from 'shared/lib/moment'

import 'react-day-picker/lib/style.css'

// Get and set locale
let locale = 'en'

if (navigator.languages !== undefined) {
  locale = navigator.languages[0]
} else {
  locale = navigator.language
}

locale = locale.toLowerCase()

import(`moment/locale/${locale}`).then(() => {
  moment.locale(locale)

  if (window.booqableOptions) {
    window.booqableOptions.locale = locale.split('-')[0]
  }
})
  .catch((error) => {
    // These locales are the default in moment and
    // not included in as specific locale files.
    // We can discard the error since US English
    // is already loaded.
    if (['en', 'en-us'].includes(locale)) return

    throw (error)
  })

class ProductAvailabilityCalendar extends Component {
  static displayName = 'ProductAvailabilityCalendar'

  static propTypes = {
    id: PropTypes.string,
    productConfiguration: PropTypes.object,
    firstDayOfWeek: PropTypes.number,
    translate: PropTypes.bool,
    selected: PropTypes.object,
    availabilities: PropTypes.object,
    locations: PropTypes.object,
    showPast: PropTypes.bool,
    showTimes: PropTypes.bool,
    fetchAvailability: PropTypes.func,
    defaultLocationId: PropTypes.string,
    t: PropTypes.func,
    showCounts: PropTypes.bool,
    operatingRules: PropTypes.object,
    isMobile: PropTypes.bool
  }

  state = {
    selectedLocationId: null,
    quantity: 1,
    date: null,
    time: null,
    containerWidth: 0
  }

  componentDidMount = () => {
    // disables animations when in test mode
    config.disabled = !!window.testMode

    this.handleFetchAvailability = debounce(
      this.fetchAvailability,
      window.testMode ? 0 : 500
    )
  }

  componentDidUpdate = (_, prevState) => {
    if (!prevState.time && this.state.time !== prevState.time && this.containerRef) {
      this.setState({ containerWidth: this.containerRef.getBoundingClientRect().width })
    }

    if (!this.state.selectedLocationId && this.props.defaultLocationId !== this.state.selectedLocationId) {
      this.setState({ selectedLocationId: this.props.defaultLocationId })
    }
  }

  handlePopoverOpen = () => {
    this.setState({ open: true, time: null })
    this.handleFetchAvailability()
  }

  handleDayClick = (date, modifiers) => {
    if (modifiers.disabled) return

    const { showTimes } = this.props

    if (!showTimes) return

    this.setState({ time: date })
  }

  handleTimeTableClose = () => {
    this.setState({ time: null })
  }

  handlePopoverClose = () => {
    this.setState({ open: false })
  }

  fetchAvailability = ({ time, timeTable } = {}) => {
    const { id, productConfiguration, selected, fetchAvailability, defaultLocationId, locations } = this.props
    const { selectedLocationId, quantity, date } = this.state
    const queryDate = time || date || (selected?.from && new Date(selected.from)) || new Date()
    const locationId = selectedLocationId || defaultLocationId || locations?.first()?.id

    fetchAvailability(id, productConfiguration, {
      date: queryDate,
      locationId,
      quantity,
      timeTable
    })
  }

  renderLocationSelect = () => {
    const { locations, defaultLocationId, t } = this.props
    const { selectedLocationId } = this.state

    if (!locations || locations.size() <= 1) return null

    return (
      <InputField
        type="select"
        data-tid="Availability location select"
        label={t('user.store.start', t('common.start.present'))}
        options={locations.pluckObject({ label: 'name', value: 'id' })}
        field={{
          name: 'location',
          value: selectedLocationId || defaultLocationId,
          onChange: (value) => this.setState({ selectedLocationId: value }, this.handleFetchAvailability)
        }}
        renderNativeOnMobile={false}
      />
    )
  }

  renderQuantityInput = () => {
    const { t } = this.props
    const { quantity } = this.state

    return (
      <InputField
        type="quantity"
        data-tid="Availability quantity"
        label={t('user.store.quantity', t('common.quantity'))}
        min={1}
        field={{
          name: 'quantity',
          value: quantity,
          onChange: (value) => { this.setState({ quantity: value }, this.handleFetchAvailability) }
        }}
      />
    )
  }

  renderDay = (day, modifiers) => {
    const { availabilities, showCounts, showPast, showTimes } = this.props

    const availabilityData = availabilities?.getFirst({ type: 'date', date: localMomentInUTC(day).format('YYYY-MM-DD') })

    return (
      <PickerDay
        day={day}
        available={availabilityData?.status}
        availableCount={availabilityData?.quantity}
        showCounts={showCounts}
        modifiers={modifiers}
        showAvailability
        showPast={showPast}
        interactive={showTimes}
      />
    )
  }

  renderAvailabilityCalendar = () => {
    const { translate, operatingRules, availabilities, showCounts, firstDayOfWeek, isMobile, showTimes, t } = this.props

    const modifiers = {}
    const selected = {
      from: new Date()
    }

    if (this.props.selected?.from) {
      Object.assign(selected, {
        from: localMoment(this.props.selected.from, true).toDate(),
        to: localMoment(this.props.selected.to, true).toDate()
      })

      Object.assign(modifiers, {
        selected,
        selectionStart: selected.from,
        selectionEnd: selected.to,
        from: selected.from,
        to: selected.to,
        past: { before: new Date() }
      })
    } else {
      Object.assign(modifiers, {
        past: { before: new Date() }
      })
    }

    return (
      <BProductAvailabilityCalendar
        isMobile={isMobile}
        ref={(ref) => { this.containerRef = ref }}
        className="booqable-component"
      >
        <BProductAvailabilityCalendar.Header onClick={this.handlePopoverClose}>
          <H3>{t('user.store.check_availability', t('common.check_availability'))}</H3>
          {' '}
          <Icon icon="times" style="fas" />
        </BProductAvailabilityCalendar.Header>
        <SwitchTransition>
          <CSSTransition
            key={(this.state.time && showTimes) ? 'time' : 'date'}
            addEndListener={(node, done) => node.addEventListener('transitionend', done, false)}
            classNames="slide-and-fade"
          >
            <>
              {this.state.time && (
                <TimeTable
                  fetchAvailability={this.fetchAvailability}
                  onClose={this.handleTimeTableClose}
                  date={this.state.time}
                  quantity={this.state.quantity}
                  width={this.state.containerWidth}
                  availabilities={availabilities}
                  operatingRules={operatingRules}
                  selected={selected}
                  showCounts={showCounts}
                />
              )}
              {!this.state.time && (
                <BProductAvailabilityCalendar.CalendarContainer>
                  <BProductAvailabilityCalendar.InputContainer>
                    {this.renderQuantityInput()}
                    {this.renderLocationSelect()}
                  </BProductAvailabilityCalendar.InputContainer>
                  <Div modifiers="padding-left-sm padding-right-sm padding-top-md padding-bottom-md">
                    <BDayPicker>
                      <DayPicker
                        navbarElement={Navigation}
                        month={selected.from || new Date()}
                        localeUtils={MomentLocaleUtils}
                        locale={translate ? locale : 'en'}
                        firstDayOfWeek={parseInt(firstDayOfWeek)}
                        renderDay={this.renderDay}
                        onMonthChange={(date) => this.setState({ date }, this.handleFetchAvailability)}
                        onDayClick={this.handleDayClick}
                        disabledDays={(date) => isDayClosed({ date: localMomentInUTC(date), openingHours: operatingRules, allowPast: false })}
                        modifiers={modifiers}
                        from={selected.from}
                        to={selected.to}
                        className={!showTimes && 'DayPicker--interactionDisabled'}
                      />
                    </BDayPicker>
                  </Div>
                </BProductAvailabilityCalendar.CalendarContainer>
              )}
            </>
          </CSSTransition>
        </SwitchTransition>
      </BProductAvailabilityCalendar>
    )
  }

  renderMobile = (open) => {
    const { t } = this.props

    return (
      <Fragment>
        <BProductAvailabilityCalendar.AvailabilityLink color="dark" size="xs" onClick={this.handlePopoverOpen}>
          {t('user.store.check_availability', t('common.check_availability'))}
        </BProductAvailabilityCalendar.AvailabilityLink>
        <PortalWrapper open={open}>
          <BDatePickerInput.MobileWrapper>
            {this.renderAvailabilityCalendar(true)}
          </BDatePickerInput.MobileWrapper>
        </PortalWrapper>
      </Fragment>
    )
  }

  render () {
    const { open } = this.state
    const { isMobile, t } = this.props

    if (isMobile) {
      return this.renderMobile(open)
    } else {
      return (
        <Popover
          content={this.renderAvailabilityCalendar}
          open={open}
          onClose={this.handlePopoverClose}
          method="manual"
          direction="right"
          fallbackPlacements={['left', 'bottom']}
          offset={20}
          fixed
        >
          <BProductAvailabilityCalendar.AvailabilityLink color="dark" size="xs" onClick={this.handlePopoverOpen}>
            {t('user.store.check_availability', t('common.check_availability'))}
          </BProductAvailabilityCalendar.AvailabilityLink>
        </Popover>
      )
    }
  }
}

const ProductAvailabilityCalendarWrapper = (props) => {
  const isMobile = useIsMobile()

  return <ProductAvailabilityCalendar {...props} isMobile={isMobile} />
}

const mapStateToProps = (state, _props) => {
  const allLocations = state.orm.locations.getAll()
  const locations = allLocations.filter((location) => location.location_type !== 'internal')

  return {
    locations,
    firstDayOfWeek: state.settings.get('dates.first_day_of_week'),
    showTimes: state.settings.get('store.use_times')
  }
}

const mapDispatchToProps = (_dispatch, _props) => ({
  fetchAvailability: (id, configuration, { date, locationId, quantity, timeTable }) => {
    if (!date) return

    const filter = {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      item_id: id,
      start_location_id: locationId,
      quantity,
      configuration
    }

    if (timeTable) {
      filter.day = date.getDate()
    }

    return AvailabilityActions.fetchItemAvailability({
      apiVersion: 3,
      filter
    })
  }
})

export default compose(
  reduxConnect(mapStateToProps, mapDispatchToProps),
  withTranslation('user')
)(ProductAvailabilityCalendarWrapper)
